// src/components/personal/sections/OrdersSection/hooks/useOrderPagination.ts
import { useState, useCallback, useMemo, useEffect } from 'react';

// Вынесенная чистая функция для расчета пагинации
export function calculatePagination(totalItems: number, pageSize: number, currentPage: number = 1) {
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const validCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const startIndex = (validCurrentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalItems);
  
  return {
    totalPages,
    currentPage: validCurrentPage,
    startIndex,
    endIndex,
    pageSize
  };
}

interface PaginationState {
  currentPage: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
  startIndex: number;
  endIndex: number;
}

interface UseOrderPaginationProps {
  totalItems: number;
  initialPage?: number;
  initialPageSize?: number;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
}

interface UseOrderPaginationReturn {
  // Состояние пагинации
  pagination: PaginationState;
  
  // Методы изменения пагинации
  setCurrentPage: (page: number) => void;
  setPageSize: (size: number) => void;
  
  // Вспомогательные методы
  nextPage: () => void;
  previousPage: () => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  
  // Вычисляемое состояние
  isFirstPage: boolean;
  isLastPage: boolean;
  pageRangeDisplay: string;
  
  // Рассчитанные индексы для текущей страницы
  startIndex: number;
  endIndex: number;
}

export const useOrderPagination = ({
  totalItems,
  initialPage = 1,
  initialPageSize = 10,
  onPageChange,
  onPageSizeChange
}: UseOrderPaginationProps): UseOrderPaginationReturn => {
  // Состояние пагинации
  const [currentPage, setCurrentPageState] = useState<number>(initialPage);
  const [pageSize, setPageSizeState] = useState<number>(initialPageSize);
  
  // Рассчитываем параметры пагинации при изменении зависимостей
  const pagination = useMemo(() => {
    const {
      totalPages,
      currentPage: validCurrentPage,
      startIndex,
      endIndex
    } = calculatePagination(totalItems, pageSize, currentPage);
    
    return {
      currentPage: validCurrentPage,
      pageSize,
      totalItems,
      totalPages,
      startIndex,
      endIndex
    };
  }, [totalItems, pageSize, currentPage]);
  
  // Обновляем текущую страницу, если она выходит за пределы при изменении общего количества
  useEffect(() => {
    if (currentPage > pagination.totalPages && pagination.totalPages > 0) {
      setCurrentPageState(pagination.totalPages);
    }
  }, [pagination.totalPages, currentPage]);
  
  // Метод для изменения текущей страницы
  const setCurrentPage = useCallback((page: number) => {
    const newPage = Math.max(1, Math.min(page, pagination.totalPages));
    setCurrentPageState(newPage);
    
    if (onPageChange) {
      onPageChange(newPage);
    }
  }, [pagination.totalPages, onPageChange]);
  
  // Метод для изменения размера страницы
  const setPageSize = useCallback((size: number) => {
    if (size > 0) {
      setPageSizeState(size);
      
      // Пересчитываем текущую страницу, чтобы сохранить положение просмотра
      const firstItemIndex = (currentPage - 1) * pageSize;
      const newPage = Math.floor(firstItemIndex / size) + 1;
      setCurrentPageState(newPage);
      
      if (onPageSizeChange) {
        onPageSizeChange(size);
      }
    }
  }, [currentPage, pageSize, onPageSizeChange]);
  
  // Переход на следующую страницу
  const nextPage = useCallback(() => {
    if (currentPage < pagination.totalPages) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, pagination.totalPages, setCurrentPage]);
  
  // Переход на предыдущую страницу
  const previousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [currentPage, setCurrentPage]);
  
  // Переход на первую страницу
  const goToFirstPage = useCallback(() => {
    setCurrentPage(1);
  }, [setCurrentPage]);
  
  // Переход на последнюю страницу
  const goToLastPage = useCallback(() => {
    setCurrentPage(pagination.totalPages);
  }, [pagination.totalPages, setCurrentPage]);
  
  // Вычисляемые свойства
  const isFirstPage = pagination.currentPage === 1;
  const isLastPage = pagination.currentPage === pagination.totalPages;
  
  // Отображение диапазона элементов
  const pageRangeDisplay = useMemo(() => {
    const start = pagination.startIndex + 1;
    const end = pagination.endIndex;
    return `${start}-${end} из ${totalItems}`;
  }, [pagination.startIndex, pagination.endIndex, totalItems]);
  
  return {
    pagination,
    setCurrentPage,
    setPageSize,
    nextPage,
    previousPage,
    goToFirstPage,
    goToLastPage,
    isFirstPage,
    isLastPage,
    pageRangeDisplay,
    startIndex: pagination.startIndex,
    endIndex: pagination.endIndex
  };
};